// REL_1.1.15 F-262 §2 + Corrigendum 2 §1.2 — Currency settings context.
// Default currency derives from detected country (hardcoded "US" → "USD" per Corrigendum 2).
// Manual override via setCurrency persists across page loads. Override path stays open for
// REL_1.3+ when country auto-detect reactivates.
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { CURRENCIES, type CurrencyCode } from "@/lib/currency";
import { detectCountry, defaultCurrencyForCountry } from "@/lib/country";

const STORAGE_KEY = "deedo-marketing-currency-override";

interface CurrencySettings {
  /** Resolved currency = override if set, else default-for-detected-country. */
  currency: CurrencyCode;
  /** Country-default currency (no override applied) — useful for "reset to default" UX. */
  defaultCurrency: CurrencyCode;
  setCurrency: (c: CurrencyCode) => void;
  isOverride: boolean;
}

const CurrencyContext = createContext<CurrencySettings | null>(null);

function readStoredOverride(): CurrencyCode | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    if (CURRENCIES.some((c) => c.code === raw)) return raw as CurrencyCode;
  } catch {
    /* localStorage unavailable (private mode / quota) — ignore */
  }
  return null;
}

export function CurrencySettingsProvider({ children }: { children: ReactNode }) {
  const [override, setOverride] = useState<CurrencyCode | null>(() => readStoredOverride());

  useEffect(() => {
    if (override === null) {
      try { window.localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    } else {
      try { window.localStorage.setItem(STORAGE_KEY, override); } catch { /* ignore */ }
    }
  }, [override]);

  const detectedCountry = detectCountry();
  const defaultCurrency = defaultCurrencyForCountry(detectedCountry);
  const currency = override ?? defaultCurrency;
  const value: CurrencySettings = {
    currency,
    defaultCurrency,
    setCurrency: setOverride,
    isOverride: override !== null,
  };

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrencySettings(): CurrencySettings {
  const ctx = useContext(CurrencyContext);
  if (!ctx) {
    throw new Error("useCurrencySettings must be used within CurrencySettingsProvider");
  }
  return ctx;
}
